import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { db } from '../firebase.js';
import { doc, getDoc, updateDoc, setDoc, collection, onSnapshot, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { FiSettings, FiUsers, FiMessageCircle, FiEye, FiHeart, 
  FiPlayCircle, FiPauseCircle, FiRefreshCw, FiCopy, FiMaximize, 
  FiHome, FiAlertCircle, FiStar, FiTrendingUp, FiMonitor, FiLayers, 
  FiTv, FiChevronRight, FiChevronLeft, FiVideo } from 'react-icons/fi';
import VideoStream from '../components/VideoStream.js';
import StreamSettings from '../components/StreamSettings';
import StreamPreview from '../components/StreamPreview';
import StreamChat from '../components/StreamChat';

// Generate a random string for stream key
const generateRandomKey = () => {
  return Math.random().toString(36).substring(2, 6) + '-' +
    Math.random().toString(36).substring(2, 6) + '-' +
    Math.random().toString(36).substring(2, 6) + '-' +
    Math.random().toString(36).substring(2, 6);
};

const StreamManager = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [streamKey, setStreamKey] = useState('');
  const [streamTitle, setStreamTitle] = useState('');
  const [streamGame, setStreamGame] = useState('');
  const [streamTags, setStreamTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isLive, setIsLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState('stream');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stats, setStats] = useState({
    viewers: 0,
    followers: 0,
    chatMessages: 0,
    subscribers: 0
  });
  const [sessionTime, setSessionTime] = useState(0);
  const [streamInfo, setStreamInfo] = useState({
    title: 'My Stream',
    category: 'Gaming',
    tags: 'gaming, live, stream',
  });
  const [activityFeed, setActivityFeed] = useState([]);
  const timerRef = useRef(null);
  const activityListenerRef = useRef(null);
  
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchStreamData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get stream document
        const streamRef = doc(db, 'streams', currentUser.uid);
        const streamDoc = await getDoc(streamRef);
        
        if (streamDoc.exists()) {
          const data = streamDoc.data();
          setStream(data);
          setStreamKey(data.streamKey || '');
          setStreamTitle(data.title || '');
          setStreamGame(data.game || '');
          setStreamTags(data.tags || []);
          setIsLive(data.status === 'live');
          setViewerCount(data.viewerCount || 0);
        } else {
          // Create new stream document if it doesn't exist
          const newStreamKey = generateRandomKey();
          const newStream = {
            userId: currentUser.uid,
            username: currentUser.displayName || currentUser.email,
            streamKey: newStreamKey,
            title: 'My Stream',
            game: '',
            tags: [],
            status: 'offline',
            viewerCount: 0,
            startedAt: null,
            createdAt: serverTimestamp()
          };
          
          await setDoc(streamRef, newStream);
          setStream(newStream);
          setStreamKey(newStreamKey);
          setIsLive(false);
          setViewerCount(0);
        }
      } catch (err) {
        console.error('Error fetching stream data:', err);
        setError('Failed to load stream data');
        toast.error('Failed to load stream data');
      } finally {
        setLoading(false);
      }
    };

    fetchStreamData();

    // Set up real-time listeners
    const streamRef = doc(db, 'streams', currentUser.uid);
    const unsubscribe = onSnapshot(streamRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setStream(data);
        setIsLive(data.status === 'live');
        setViewerCount(data.viewerCount || 0);
      }
    }, (err) => {
      console.error('Error in stream listener:', err);
      setError('Failed to get real-time updates');
      toast.error('Failed to get real-time updates');
    });

    return () => {
      unsubscribe();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (activityListenerRef.current) {
        activityListenerRef.current();
      }
    };
  }, [currentUser, navigate]);

  // Handle going live and stream timer
  useEffect(() => {
    // Listen for stream activity (followers, chat, etc)
    const startActivityListener = () => {
      if (!currentUser) return;
      
      try {
        // Create reference to stream activity collection
        const activityRef = collection(db, 'streamActivity', currentUser.uid, 'events');
        
        // Create query for recent activities, ordered by timestamp
        const activityQuery = query(
          activityRef,
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        
        // Set up real-time listener
        activityListenerRef.current = onSnapshot(activityQuery, (snapshot) => {
          const activities = [];
          
          snapshot.forEach(doc => {
            const data = doc.data();
            activities.push({
              id: doc.id,
              ...data,
              // Convert Firestore timestamp to JS Date
              timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date()
            });
          });
          
          setActivityFeed(activities);
          
          // Update stats based on activity data
          if (activities.length > 0) {
            // This is a simple example - in a real app, you might want to
            // query dedicated stat documents instead
            const newFollowers = activities.filter(a => a.type === 'follow').length;
            const newMessages = activities.filter(a => a.type === 'chat').length;
            const newSubscribers = activities.filter(a => a.type === 'subscribe').length;
            
            // Update stats (in a real app, you'd get exact counts from Firestore)
            setStats(prev => ({
              ...prev,
              followers: prev.followers + newFollowers,
              chatMessages: prev.chatMessages + newMessages,
              subscribers: prev.subscribers + newSubscribers,
              // For viewers, you might want a separate collection that tracks current viewers
              viewers: Math.max(5, prev.viewers + Math.floor(Math.random() * 3) - 1)
            }));
          }
        }, (error) => {
          console.error("Error listening to stream activity:", error);
        });
      } catch (error) {
        console.error("Error setting up activity listener:", error);
      }
    };
    
    if (isLive) {
      // Update stream time every second
      timerRef.current = setInterval(() => {
        setSessionTime(prev => prev + 1);
      }, 1000);
      
      // Start activity listener
      startActivityListener();
    } else if (timerRef.current) {
      // Clear timer if not live
      clearInterval(timerRef.current);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isLive, currentUser, db]);
  
  const generateStreamKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = '';
    for (let i = 0; i < 32; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  };

  const handleStartStream = async () => {
    if (!streamTitle.trim()) {
      setError('Please enter a stream title');
      return;
    }
    
    try {
      const streamRef = doc(db, 'streams', currentUser.uid);
      await updateDoc(streamRef, {
        title: streamTitle,
        game: streamGame,
        tags: streamTags,
        status: 'live',
        startedAt: serverTimestamp(),
        viewerCount: 0
      });
      
      setIsLive(true);
      setError(null);
    } catch (err) {
      console.error('Error starting stream:', err);
      setError('Failed to start stream');
    }
  };

  const handleEndStream = async () => {
    try {
      const streamRef = doc(db, 'streams', currentUser.uid);
      await updateDoc(streamRef, {
        status: 'offline',
        startedAt: null,
        viewerCount: 0
      });
      
      setIsLive(false);
      setError(null);
    } catch (err) {
      console.error('Error ending stream:', err);
      setError('Failed to end stream');
    }
  };

  const handleUpdateStream = async (updatedStream) => {
    if (!updatedStream.title.trim()) {
      setError('Please enter a stream title');
      return;
    }
    
    try {
      const streamRef = doc(db, 'streams', currentUser.uid);
      await updateDoc(streamRef, {
        title: updatedStream.title,
        game: updatedStream.game,
        tags: updatedStream.tags,
        status: updatedStream.status,
        startedAt: updatedStream.startedAt,
        viewerCount: updatedStream.viewerCount
      });
      
      setStream(updatedStream);
      setError(null);
    } catch (err) {
      console.error('Error updating stream:', err);
      setError('Failed to update stream');
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (!tagInput.trim()) return;
    
    const newTag = tagInput.trim().toLowerCase();
    if (!streamTags.includes(newTag)) {
      setStreamTags([...streamTags, newTag]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove) => {
    setStreamTags(streamTags.filter(tag => tag !== tagToRemove));
  };

  const handleCopyStreamKey = () => {
    navigator.clipboard.writeText(streamKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerateStreamKey = async () => {
    try {
      const newStreamKey = generateStreamKey();
      const streamRef = doc(db, 'streams', currentUser.uid);
      await updateDoc(streamRef, {
        streamKey: newStreamKey
      });
      
      setStreamKey(newStreamKey);
      setError(null);
    } catch (err) {
      console.error('Error regenerating stream key:', err);
      setError('Failed to regenerate stream key');
    }
  };
  
  const sidebarItems = [
    { id: 'home', icon: <FiHome size={20} />, label: 'Home' },
    { id: 'stream', icon: <FiTv size={20} />, label: 'Stream Manager' },
    { id: 'alerts', icon: <FiAlertCircle size={20} />, label: 'Alerts' },
    { id: 'community', icon: <FiUsers size={20} />, label: 'Community' },
    { id: 'goals', icon: <FiStar size={20} />, label: 'Goals' },
    { id: 'analytics', icon: <FiTrendingUp size={20} />, label: 'Analytics' },
    { id: 'content', icon: <FiMonitor size={20} />, label: 'Content' },
    { id: 'extensions', icon: <FiLayers size={20} />, label: 'Extensions' },
    { id: 'settings', icon: <FiSettings size={20} />, label: 'Settings' }
  ];
  
  // Mock chat messages - in a real app, you'd use Firestore for this
  const chatMessages = [
    { user: 'GamerPro123', message: 'Nice stream today!', time: '2m ago' },
    { user: 'StreamFan99', message: 'When is the next tournament?', time: '5m ago' },
    { user: 'EpicViewer', message: 'You should try that new strategy', time: '7m ago' },
    { user: 'Moderator_Sam', message: 'Welcome everyone to the stream!', time: '10m ago', mod: true },
    { user: 'NewFollower', message: 'Just followed! Love the content', time: '12m ago' }
  ];
  
  // Quick action buttons
  const quickActions = [
    { icon: <FiPlayCircle size={18} />, label: 'Go Live', action: handleStartStream, hidden: isLive },
    { icon: <FiPauseCircle size={18} />, label: 'End Stream', action: handleEndStream, hidden: !isLive },
    { icon: <FiRefreshCw size={18} />, label: 'Refresh Key', action: handleRegenerateStreamKey },
    { icon: <FiCopy size={18} />, label: 'Copy Key', action: handleCopyStreamKey }
  ];
  
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-64 bg-gray-800 rounded-lg mb-6"></div>
          <div className="h-48 bg-gray-800 rounded-lg"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-500/10 text-red-500 p-6 rounded-lg flex items-start">
          <FiAlertCircle className="w-6 h-6 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium mb-1">Error</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Render appropriate content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'home':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Dashboard Home</h2>
            <p className="mb-4 text-harmony-white/70">Welcome to your StreamerWars dashboard! Navigate using the sidebar to manage your stream.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Quick Links</h3>
                <ul className="space-y-2 text-harmony-white/70">
                  <li><button onClick={() => setActiveSection('stream')} className="text-harmony-orange hover:underline">Stream Manager</button></li>
                  <li><button onClick={() => setActiveSection('analytics')} className="text-harmony-orange hover:underline">View Analytics</button></li>
                  <li><button onClick={() => setActiveSection('community')} className="text-harmony-orange hover:underline">Community Management</button></li>
                </ul>
              </div>
            </div>
          </div>
        );
      
      case 'alerts':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Alert Management</h2>
            <p className="text-harmony-white/70 mb-4">Customize your stream alerts and notifications.</p>
            <div className="space-y-4">
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Alert Types</h3>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input type="checkbox" id="followers" className="mr-2" defaultChecked />
                    <label htmlFor="followers">New Followers</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" id="donations" className="mr-2" defaultChecked />
                    <label htmlFor="donations">Donations</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" id="subscribers" className="mr-2" defaultChecked />
                    <label htmlFor="subscribers">New Subscribers</label>
                  </div>
                </div>
              </div>
              <button className="bg-harmony-orange hover:bg-harmony-orange/90 px-4 py-2 rounded text-white">
                Save Alert Settings
              </button>
            </div>
          </div>
        );
      
      case 'community':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Community Management</h2>
            <p className="text-harmony-white/70 mb-4">Manage your community, moderators, and chat settings.</p>
            <div className="bg-harmony-blue p-4 rounded-lg mb-4">
              <h3 className="font-medium mb-2">Moderators</h3>
              <ul className="space-y-2">
                <li className="flex justify-between items-center text-harmony-white/70 p-2 hover:bg-harmony-blue-light/20 rounded">
                  <span>Moderator_Sam</span>
                  <button className="text-red-400 hover:text-red-300">Remove</button>
                </li>
                <li className="flex justify-between items-center text-harmony-white/70 p-2 hover:bg-harmony-blue-light/20 rounded">
                  <span>TrustedMod42</span>
                  <button className="text-red-400 hover:text-red-300">Remove</button>
                </li>
              </ul>
              <button className="mt-2 bg-harmony-blue-light/30 hover:bg-harmony-blue-light/50 px-3 py-1 rounded text-sm">
                Add Moderator
              </button>
            </div>
          </div>
        );
      
      case 'goals':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Stream Goals</h2>
            <p className="text-harmony-white/70 mb-4">Set and track goals for your streams.</p>
            <div className="space-y-4">
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Followers Goal</h3>
                <div className="w-full bg-harmony-blue-light/30 h-4 rounded overflow-hidden">
                  <div className="bg-harmony-orange h-full" style={{width: '65%'}}></div>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>65/100</span>
                  <span>65%</span>
                </div>
              </div>
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Subscribers Goal</h3>
                <div className="w-full bg-harmony-blue-light/30 h-4 rounded overflow-hidden">
                  <div className="bg-harmony-orange h-full" style={{width: '30%'}}></div>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span>15/50</span>
                  <span>30%</span>
                </div>
              </div>
              <button className="bg-harmony-orange hover:bg-harmony-orange/90 px-4 py-2 rounded text-white">
                Add New Goal
              </button>
            </div>
          </div>
        );
      
      case 'analytics':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Stream Analytics</h2>
            <p className="text-harmony-white/70 mb-4">View performance metrics for your streams.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Viewer Statistics</h3>
                <div className="text-4xl font-bold">{stats.viewers}</div>
                <div className="text-harmony-white/50 text-sm">Current viewers</div>
              </div>
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Stream Duration</h3>
                <div className="text-4xl font-bold">{formatTime(sessionTime)}</div>
                <div className="text-harmony-white/50 text-sm">Current stream time</div>
              </div>
            </div>
            <div className="bg-harmony-blue p-4 rounded-lg">
              <h3 className="font-medium mb-2">Recent Growth</h3>
              <div className="h-40 bg-harmony-blue-light/20 rounded flex items-end justify-between p-2">
                <div className="w-1/7 bg-harmony-orange h-1/5 rounded-t"></div>
                <div className="w-1/7 bg-harmony-orange h-3/5 rounded-t"></div>
                <div className="w-1/7 bg-harmony-orange h-2/5 rounded-t"></div>
                <div className="w-1/7 bg-harmony-orange h-4/5 rounded-t"></div>
                <div className="w-1/7 bg-harmony-orange h-3/5 rounded-t"></div>
                <div className="w-1/7 bg-harmony-orange h-2/5 rounded-t"></div>
                <div className="w-1/7 bg-harmony-orange h-3/5 rounded-t"></div>
              </div>
              <div className="flex justify-between text-xs mt-2 text-harmony-white/50">
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Sun</span>
              </div>
            </div>
          </div>
        );
      
      case 'content':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Content Management</h2>
            <p className="text-harmony-white/70 mb-4">Manage your stream content and uploads.</p>
            <div className="space-y-4">
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Recent Broadcasts</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 hover:bg-harmony-blue-light/20 rounded">
                    <span>Tournament Finals - 05/12/2023</span>
                    <div className="flex space-x-2">
                      <button className="text-harmony-white/70 hover:text-harmony-white">Edit</button>
                      <button className="text-harmony-white/70 hover:text-harmony-white">Download</button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-harmony-blue-light/20 rounded">
                    <span>Casual Friday Stream - 05/10/2023</span>
                    <div className="flex space-x-2">
                      <button className="text-harmony-white/70 hover:text-harmony-white">Edit</button>
                      <button className="text-harmony-white/70 hover:text-harmony-white">Download</button>
                    </div>
                  </div>
                </div>
              </div>
              <button className="bg-harmony-orange hover:bg-harmony-orange/90 px-4 py-2 rounded text-white">
                Upload Content
              </button>
            </div>
          </div>
        );
      
      case 'extensions':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Stream Extensions</h2>
            <p className="text-harmony-white/70 mb-4">Add and manage stream extensions and integrations.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-harmony-blue p-4 rounded-lg flex items-center">
                <div className="mr-4 bg-harmony-blue-light/30 w-12 h-12 rounded flex items-center justify-center">
                  <FiStar size={24} />
                </div>
                <div>
                  <h3 className="font-medium">Polls & Voting</h3>
                  <div className="text-harmony-white/50 text-sm">Create interactive polls</div>
                  <button className="text-harmony-orange text-sm hover:underline mt-1">Install</button>
                </div>
              </div>
              <div className="bg-harmony-blue p-4 rounded-lg flex items-center">
                <div className="mr-4 bg-harmony-blue-light/30 w-12 h-12 rounded flex items-center justify-center">
                  <FiAlertCircle size={24} />
                </div>
                <div>
                  <h3 className="font-medium">Custom Alerts</h3>
                  <div className="text-harmony-white/50 text-sm">Personalized stream alerts</div>
                  <div className="text-green-400 text-sm mt-1">Installed</div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'settings':
        return (
          <div className="bg-harmony-blue-dark rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">Stream Settings</h2>
            <p className="text-harmony-white/70 mb-4">Configure your stream settings and preferences.</p>
            <div className="space-y-4">
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Stream Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-harmony-white/70 mb-1">Stream Title</label>
                    <input
                      type="text"
                      name="title"
                      value={streamInfo.title}
                      onChange={(e) => setStreamInfo(prev => ({
                        ...prev,
                        title: e.target.value
                      }))}
                      className="w-full bg-harmony-blue-light/30 border border-harmony-white/10 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-harmony-white/70 mb-1">Game/Category</label>
                    <input
                      type="text"
                      name="category"
                      value={streamInfo.category}
                      onChange={(e) => setStreamInfo(prev => ({
                        ...prev,
                        category: e.target.value
                      }))}
                      className="w-full bg-harmony-blue-light/30 border border-harmony-white/10 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-harmony-white/70 mb-1">Stream Tags</label>
                    <input
                      type="text"
                      name="tags"
                      value={streamInfo.tags}
                      onChange={(e) => setStreamInfo(prev => ({
                        ...prev,
                        tags: e.target.value.split(',').map(tag => tag.trim())
                      }))}
                      className="w-full bg-harmony-blue-light/30 border border-harmony-white/10 rounded px-3 py-2"
                      placeholder="e.g., esports, gaming, tournament (comma separated)"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Stream Key</h3>
                <div className="flex">
                  <input 
                    type="password" 
                    value={streamKey} 
                    readOnly 
                    className="flex-1 bg-harmony-blue-light/30 border border-harmony-white/10 rounded-l px-3 py-2"
                  />
                  <button 
                    onClick={handleCopyStreamKey}
                    className="bg-harmony-blue-light/40 hover:bg-harmony-blue-light/60 px-3 py-2 rounded-r"
                  >
                    <FiCopy size={16} />
                  </button>
                </div>
                <div className="mt-2 flex items-center">
                  <button 
                    onClick={handleRegenerateStreamKey}
                    className="text-sm text-harmony-orange hover:underline flex items-center"
                  >
                    <FiRefreshCw size={14} className="mr-1" />
                    Generate New Stream Key
                  </button>
                  <span className="text-xs text-harmony-white/50 ml-3">
                    Warning: This will invalidate your current stream key
                  </span>
                </div>
              </div>
              <div className="bg-harmony-blue p-4 rounded-lg">
                <h3 className="font-medium mb-2">Stream URL</h3>
                <div className="text-sm text-harmony-white/70 mb-2">
                  Use these settings in your streaming software (OBS, Streamlabs, etc.)
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-harmony-white/50 mb-1">Stream URL</label>
                    <div className="flex">
                      <input 
                        type="text" 
                        value="rtmp://stream.streamerwars.com/live" 
                        readOnly 
                        className="flex-1 bg-harmony-blue-light/30 border border-harmony-white/10 rounded-l px-3 py-2 text-sm"
                      />
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText("rtmp://stream.streamerwars.com/live");
                          toast.success("Stream URL copied to clipboard");
                        }}
                        className="bg-harmony-blue-light/40 hover:bg-harmony-blue-light/60 px-3 py-2 rounded-r"
                      >
                        <FiCopy size={16} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-harmony-white/50 mb-1">Stream Key</label>
                    <div className="flex">
                      <input 
                        type="password" 
                        value={streamKey} 
                        readOnly 
                        className="flex-1 bg-harmony-blue-light/30 border border-harmony-white/10 rounded-l px-3 py-2 text-sm"
                      />
                      <button 
                        onClick={handleCopyStreamKey}
                        className="bg-harmony-blue-light/40 hover:bg-harmony-blue-light/60 px-3 py-2 rounded-r"
                      >
                        <FiCopy size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setStreamInfo(streamInfo)} 
                className="bg-harmony-orange hover:bg-harmony-orange/90 px-4 py-2 rounded text-white"
              >
                Save Settings
              </button>
            </div>
          </div>
        );
      
      case 'stream':
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <StreamPreview
                stream={stream}
                onStartStream={handleStartStream}
                onEndStream={handleEndStream}
              />
            </div>
            
            <div className="space-y-6">
              <StreamSettings
                stream={stream}
                onUpdate={handleUpdateStream}
              />
              
              <StreamChat streamId={stream.id} />
            </div>
          </div>
        );
    }
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-harmony-blue-light text-harmony-white pt-12">
      {/* Header */}
      <div className="bg-harmony-blue-dark p-4 border-b border-harmony-blue">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">{sidebarItems.find(item => item.id === activeSection)?.label || "Stream Manager"}</h1>
          <div className="flex space-x-4 items-center">
            <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center ${isLive ? 'bg-red-600' : 'bg-gray-600'}`}>
              <span className={`h-2 w-2 rounded-full mr-2 ${isLive ? 'bg-harmony-blue-light' : 'bg-gray-400'}`}></span>
              {isLive ? 'LIVE' : 'OFFLINE'}
            </div>
            <div className="text-sm">{formatTime(sessionTime)}</div>
            <button className="bg-harmony-orange hover:bg-harmony-orange-light text-harmony-white px-4 py-2 rounded-md font-medium" onClick={() => setActiveSection('settings')}>
              Settings
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex flex-1">
        {/* Sidebar */}
        <div className={`bg-harmony-blue-dark border-r border-harmony-blue transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-60'}`}>
          <div className="p-4 border-b border-harmony-blue flex justify-between items-center">
            <h2 className={`font-medium transition-opacity duration-300 ${sidebarCollapsed ? 'opacity-0' : 'opacity-100'}`}>
              Channel
            </h2>
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="text-harmony-white opacity-75 hover:opacity-100">
              {sidebarCollapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
            </button>
          </div>
          <div className="py-4">
            {sidebarItems.map((item, index) => (
              <div 
                key={index} 
                className={`flex items-center px-4 py-3 ${item.id === activeSection ? 'bg-harmony-blue text-harmony-orange' : 'text-harmony-white hover:bg-harmony-blue hover:bg-opacity-50'} cursor-pointer`}
                onClick={() => setActiveSection(item.id)}
              >
                <div className="mr-3">{item.icon}</div>
                <span className={`transition-opacity duration-300 ${sidebarCollapsed ? 'opacity-0 hidden' : 'opacity-100'}`}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 p-4 overflow-auto bg-harmony-blue-light">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default StreamManager; 